import { useInfiniteQuery } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useFragmentValue, useHaloScript, usePageFragment } from '../../hooks';
import { queryKeys } from '../../queryKeys';

import type {
  CursorPaginationOptions,
  ElemasonOrderLiteWidget,
  OrderLite,
} from '@tectonic/types';

const getOrderLite = async (
  orderId: string,
  params?: CursorPaginationOptions
): Promise<OrderLite> => {
  const response = await remixApi.getMinimalOrder({ ...params, orderId });
  if (response.error || isNil(response.data)) {
    throw response.error;
  }
  return response.data;
};

const useOrderLite = (widget: ElemasonOrderLiteWidget) => {
  const wData = widget.data!;
  const initialOrder = useHaloScript<OrderLite>(wData.order)!;

  const first = initialOrder.lineItems.edges.length;
  const initialPageParam: CursorPaginationOptions = {
    after: initialOrder.lineItems.pageInfo.endCursor,
  };

  const {
    data: response,
    hasNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
  } = useInfiniteQuery({
    initialPageParam,
    enabled: initialOrder.lineItems.pageInfo.hasNextPage,
    initialData: { pages: [initialOrder.lineItems], pageParams: [] },
    queryFn: async ({ pageParam }) => {
      const orderLite = await getOrderLite(initialOrder.id, pageParam);
      return orderLite.lineItems;
    },
    queryKey: [queryKeys.orders(), initialOrder.id],
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage) {
        return { after: lastPage.pageInfo.endCursor, first };
      }
      return null;
    },
  });

  const order = useMemo(() => {
    if (!response) {
      return initialOrder;
    }

    const lineItems = {
      edges: response.pages.flatMap((page) => page.edges),
      pageInfo: response.pages[response.pages.length - 1].pageInfo,
    };

    return { ...initialOrder, lineItems };
  }, [response, initialOrder]);

  const fragment = usePageFragment(wData.fragment);
  const fragmentValue = useFragmentValue(fragment);
  const fragmentData = fragmentValue({
    order,
    isError,
    hasNextPage,
    onLoadMore,
    isLoading: isLoading || isFetchingNextPage,
  });

  return { fragmentData, fragment };
};

export { useOrderLite };
